// /app/frontend/src/components/MediaSelector.jsx
/**
 * MediaSelector - A reusable component to select media from the gallery
 * Supports single and multiple selection modes
 */
import React, { useState, useEffect } from 'react';
import { 
  X, Check, Search, Image, Video, FolderOpen, 
  Handshake, Gift, ImageIcon, Plus
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { getImageUrl } from '../lib/utils';

const ICON_MAP = {
  'handshake': Handshake,
  'image': ImageIcon,
  'gift': Gift,
  'folder': FolderOpen,
};

export default function MediaSelector({ 
  open, 
  onClose, 
  onSelect, 
  multiple = false,
  categoryFilter = null, // Filter by category name (e.g., "Logos partenaires")
  mediaTypeFilter = null, // "image" or "video"
  title = "Sélectionner un média"
}) {
  const [categories, setCategories] = useState([]);
  const [media, setMedia] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    if (open) {
      loadData();
    } else {
      // Reset selection when closing
      setSelectedItems([]);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      loadMedia();
    }
  }, [selectedCategory, open]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/media/categories');
      setCategories(res.data);
      
      // If categoryFilter is set, find and select that category
      if (categoryFilter) {
        const filtered = res.data.find(c => 
          c.name.toLowerCase().includes(categoryFilter.toLowerCase())
        );
        if (filtered) {
          setSelectedCategory(filtered.id);
        }
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadMedia = async () => {
    try {
      let params = [];
      if (selectedCategory) params.push(`category_id=${selectedCategory}`);
      if (mediaTypeFilter) params.push(`media_type=${mediaTypeFilter}`);
      
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      const res = await api.get(`/api/media${queryString}`);
      setMedia(res.data);
    } catch (error) {
      console.error('Error loading media:', error);
    }
  };

  const toggleSelection = (item) => {
    if (multiple) {
      setSelectedItems(prev => 
        prev.some(i => i.id === item.id)
          ? prev.filter(i => i.id !== item.id)
          : [...prev, item]
      );
    } else {
      setSelectedItems([item]);
    }
  };

  const handleConfirm = () => {
    if (selectedItems.length === 0) {
      toast.error('Sélectionnez au moins un média');
      return;
    }
    
    if (multiple) {
      onSelect(selectedItems);
    } else {
      onSelect(selectedItems[0]);
    }
    onClose();
  };

  const filteredMedia = media.filter(m => 
    m.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 gap-4">
          {/* Sidebar - Categories */}
          {!categoryFilter && (
            <div className="w-48 border-r dark:border-gray-700 pr-4 overflow-y-auto">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg mb-1 text-sm transition-colors ${
                  selectedCategory === null 
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                <FolderOpen className="h-4 w-4" />
                <span>Tous</span>
              </button>

              {categories.map(cat => {
                const IconComponent = ICON_MAP[cat.icon] || FolderOpen;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg mb-1 text-sm transition-colors ${
                      selectedCategory === cat.id 
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <IconComponent className="h-4 w-4" />
                    <span className="truncate">{cat.name}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Main content */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Media grid */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                </div>
              ) : filteredMedia.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Image className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Aucun média disponible</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-4"
                    onClick={() => window.open('/media', '_blank')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter des médias
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-3">
                  {filteredMedia.map(item => {
                    const isSelected = selectedItems.some(i => i.id === item.id);
                    return (
                      <div 
                        key={item.id}
                        onClick={() => toggleSelection(item)}
                        className={`relative aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 cursor-pointer border-2 transition-all ${
                          isSelected 
                            ? 'border-blue-500 ring-2 ring-blue-200' 
                            : 'border-transparent hover:border-gray-300'
                        }`}
                      >
                        {item.media_type === 'video' ? (
                          <video 
                            src={getImageUrl(item.url)} 
                            className="w-full h-full object-cover"
                            muted
                          />
                        ) : (
                          <img 
                            src={getImageUrl(item.url)} 
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                        
                        {/* Selection indicator */}
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="h-4 w-4 text-white" />
                          </div>
                        )}
                        
                        {/* Type indicator */}
                        <div className="absolute bottom-2 left-2">
                          {item.media_type === 'video' ? (
                            <Video className="h-4 w-4 text-white drop-shadow" />
                          ) : null}
                        </div>
                        
                        {/* Name on hover */}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 hover:opacity-100 transition-opacity">
                          <p className="text-xs text-white truncate">{item.name}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 pt-4 border-t dark:border-gray-700">
          <div className="flex items-center justify-between w-full">
            <span className="text-sm text-gray-500">
              {selectedItems.length > 0 
                ? `${selectedItems.length} média(s) sélectionné(s)` 
                : 'Aucune sélection'}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={handleConfirm} disabled={selectedItems.length === 0}>
                {multiple ? 'Ajouter la sélection' : 'Sélectionner'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
